import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarRange, Download, Search, ArrowDownLeft, ArrowUpRight,
  Scale, Package, Info, ChevronDown,
} from "lucide-react";
import api, { money, errMsg } from "../lib/api";
import { todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";
import { toast } from "sonner";

// The 21 Aug 2026 stock sheet is the frozen opening. Nothing before this date
// can be reconstructed, so the date pickers refuse it.
const ANCHOR = "2026-08-21";

// The four stock categories. The category filter is a MULTI-select over these;
// an empty selection means "all categories".
const CATEGORIES = ["Retail", "Technical", "Disposable", "Electrical"];

function shiftDays(iso, days) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function monthStart(iso) {
  return iso.slice(0, 7) + "-01";
}

function lastMonthRange(iso) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  const start = d.toISOString().slice(0, 10);
  const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
    .toISOString()
    .slice(0, 10);
  return [start, end];
}

function fmtIn(n) {
  const v = Number(n) || 0;
  if (v > 0) return `+${v.toLocaleString()}`;
  if (v < 0) return `−${Math.abs(v).toLocaleString()}`;
  return "—";
}

export default function StockHistory() {
  const today = todayIST();
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [cats, setCats] = useState([]); // multi-select; empty = all categories
  const [openCats, setOpenCats] = useState(false); // category dropdown open
  const catsRef = useRef(null);
  const [movement, setMovement] = useState("all"); // all | in | out | moved

  const fetchSeq = useRef(0);
  const fetchHistory = async (silent = false) => {
    // Every range change (and every background refresh) gets its own sequence
    // number. A slow response for the OLD date range must never overwrite the
    // NEW one — that race is what made the Closing KPI/table briefly show the
    // live/current figures after the To date had been changed.
    const seq = ++fetchSeq.current;
    if (!from || !to) return;
    if (from < ANCHOR) {
      setError(`History starts on ${ANCHOR}.`);
      return;
    }
    if (from > to) {
      setError("From date cannot be after To date.");
      return;
    }
    if (!silent) setLoading(true);
    try {
      const res = await api.get(
        `/inventory/history?date_from=${from}&date_to=${to}`
      );
      if (seq !== fetchSeq.current) return; // a newer range was requested
      setData(res.data);
      setError("");
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setError(errMsg(e));
    } finally {
      if (!silent && seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useAutoRefresh(() => fetchHistory(true), 60000);

  // The category multi-select closes on an outside click (same rule as the
  // app's modals) so it can never be left open over the table.
  useEffect(() => {
    if (!openCats) return;
    const onDoc = (e) => {
      if (catsRef.current && !catsRef.current.contains(e.target)) setOpenCats(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openCats]);

  const rows = data?.rows || [];
  const summary = data?.summary || {
    products: 0, opening: 0, in: 0, out: 0, closing: 0, value: 0, consumption_value: 0,
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hit =
          (r.name || "").toLowerCase().includes(q) ||
          (r.category || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (cats.length > 0 && !cats.includes(r.category || "")) return false;
      const hasIn = Number(r.in) > 0;
      const hasOut = Number(r.out) > 0;
      if (movement === "in" && !hasIn) return false;
      if (movement === "out" && !hasOut) return false;
      if (movement === "moved" && !hasIn && !hasOut) return false;
      return true;
    });
  }, [rows, search, cats, movement]);

  const hasFilters =
    Boolean(search.trim()) || cats.length > 0 || movement !== "all";

  const clearFilters = () => {
    setSearch("");
    setCats([]);
    setMovement("all");
  };

  const toggleCat = (c) =>
    setCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const filteredTotals = useMemo(() => {
    return filtered.reduce(
      (acc, r) => {
        acc.opening += Number(r.opening) || 0;
        acc.opening_value += Number(r.opening_value !== undefined ? r.opening_value : (Number(r.opening || 0) * Number(r.unit_price || 0))) || 0;
        acc.in += Number(r.in) || 0;
        acc.in_value += Number(r.in_value !== undefined ? r.in_value : (Number(r.in || 0) * Number(r.unit_price || 0))) || 0;
        acc.out += Number(r.out) || 0;
        acc.closing += Number(r.closing) || 0;
        acc.value += Number(r.value) || 0;
        acc.consumption += Number(r.consumption_value) || 0;
        return acc;
      },
      { opening: 0, opening_value: 0, in: 0, in_value: 0, out: 0, closing: 0, value: 0, consumption: 0 }
    );
  }, [filtered]);

  // The KPI cards follow the table filters. With no filter they show the
  // server's range totals (which include products whose only movement falls
  // outside the range); with a search / category / movement filter active they
  // show the totals of exactly the rows on screen, so the cards and the table
  // can never disagree.
  const kpi = hasFilters
    ? {
        opening: filteredTotals.opening,
        opening_value: filteredTotals.opening_value,
        in: filteredTotals.in,
        in_value: filteredTotals.in_value,
        out: filteredTotals.out,
        closing: filteredTotals.closing,
        value: filteredTotals.value,
        consumption_value: filteredTotals.consumption,
        cogs_value: filteredTotals.opening_value + filteredTotals.in_value - filteredTotals.value,
      }
    : summary;

  const applyRange = (a, b) => {
    setFrom(a);
    setTo(b);
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const headers = [
      "Product", "Category", "Opening", "In", "In Value", "Out", "Closing",
      "Stock Unit", "Unit Price", "Consumption Value",
    ];
    const body = filtered.map((r) =>
      [
        `"${(r.name || "").replace(/"/g, '""')}"`,
        `"${(r.category || "").replace(/"/g, '""')}"`,
        r.opening,
        r.in,
        r.in_value !== undefined ? r.in_value : (Number(r.in || 0) * Number(r.unit_price || 0)),
        r.out,
        r.closing,
        `"${(r.stock_unit || r.unit || "").replace(/"/g, '""')}"`,
        r.unit_price,
        r.consumption_value,
      ].join(",")
    );
    const blob = new Blob([[headers.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Geetanjali_Stock_History_${from}_to_${to}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} products.`);
  };

  const presets = [
    { label: "Today", run: () => applyRange(today, today) },
    { label: "Last 7 days", run: () => applyRange(shiftDays(today, -6), today) },
    { label: "This month", run: () => applyRange(monthStart(today), today) },
    {
      label: "Last month",
      run: () => {
        const [a, b] = lastMonthRange(today);
        applyRange(a < ANCHOR ? ANCHOR : a, b < ANCHOR ? ANCHOR : b);
      },
    },
    { label: "Full history", run: () => applyRange(ANCHOR, today) },
  ];

  const liveNote =
    data?.live?.is_current && Math.abs(Number(data.live.difference) || 0) > 0.004;

  return (
    <div className="space-y-6">
      {/* Range picker */}
      <div className="lss-card p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="lss-overline text-amber-800">Range Viewer</div>
            <h2 className="font-serif-lux text-2xl font-bold tracking-tight text-slate-950 mt-0.5 flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-amber-700" /> Stock History
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Opening balance from the 21 Aug 2026 stock sheet, then every
              purchase, stock-out and sale counted on its own date.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="lss-overline block text-slate-500 mb-1">From</span>
              <input
                type="date"
                min={ANCHOR}
                max={today}
                value={from}
                onChange={(e) => {
                  const v = e.target.value;
                  setFrom(v);
                  if (v && to && v > to) setTo(v);
                }}
                className="lss-input text-xs font-semibold"
              />
            </label>
            <label className="block">
              <span className="lss-overline block text-slate-500 mb-1">To</span>
              <input
                type="date"
                min={ANCHOR}
                max={today}
                value={to}
                onChange={(e) => {
                  const v = e.target.value;
                  setTo(v);
                  if (v && from && v < from) setFrom(v);
                }}
                className="lss-input text-xs font-semibold"
              />
            </label>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!filtered.length}
              className="px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md flex items-center gap-1.5 transition-all disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-3 border-t border-slate-200">
          <span className="lss-overline text-slate-400 mr-1">Quick range</span>
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={p.run}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-700 bg-slate-100 hover:bg-slate-950 hover:text-amber-300 transition-all"
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Product filters live in the heading, so the range and the rows it
            narrows are in one place. Everything below reactively filters the
            table and the footer totals. */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="lss-overline text-amber-800">Filter products</span>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 hover:text-rose-800"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <label className="block lg:col-span-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search product or category..."
                  className="lss-input lss-search w-full text-xs font-semibold"
                />
              </div>
            </label>
            <div className="block relative" ref={catsRef}>
              <button
                type="button"
                onClick={() => setOpenCats((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={openCats}
                data-testid="category-multiselect"
                className="lss-input w-full text-xs font-semibold flex items-center justify-between gap-2 text-left"
              >
                <span className="truncate">
                  {cats.length === 0 || cats.length === CATEGORIES.length
                    ? "All categories"
                    : cats.length === 1
                    ? cats[0]
                    : `${cats[0]} +${cats.length - 1}`}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${openCats ? "rotate-180" : ""}`}
                />
              </button>
              {openCats && (
                <div className="absolute z-30 mt-1 left-0 w-full min-w-[180px] bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 space-y-0.5">
                  <button
                    type="button"
                    onClick={() => setCats([])}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-extrabold uppercase tracking-wide transition ${
                      cats.length === 0 ? "bg-slate-950 text-amber-300" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    All categories
                  </button>
                  <div className="h-px bg-slate-200 my-1" />
                  {CATEGORIES.map((c) => {
                    const on = cats.includes(c);
                    return (
                      <label
                        key={c}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleCat(c)}
                          className="h-3.5 w-3.5 rounded border-slate-300 accent-amber-600"
                        />
                        <span className={on ? "text-slate-950" : ""}>{c}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <label className="block">
              <select
                value={movement}
                onChange={(e) => setMovement(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
              >
                <option value="all">All items</option>
                <option value="in">Only In items</option>
                <option value="out">Only Out items</option>
                <option value="moved">Only moved items (In or Out)</option>
              </select>
            </label>
          </div>
          {hasFilters && (
            <p className="text-[10px] font-bold text-slate-500 mt-2">
              Showing {filtered.length} of {rows.length} products · totals below
              reflect the filter.
            </p>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-semibold text-rose-800">
            {error}
          </div>
        )}
      </div>

      {hasFilters && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-900">
          KPIs reflect your filter — {filtered.length} of {rows.length} products
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="lss-card p-4">
          <span className="lss-overline block text-slate-500">Opening</span>
          <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">
            {Number(kpi.opening || 0).toLocaleString()}
          </span>
          <span className="text-[10px] font-bold text-slate-400">units</span>
        </div>
        <div className="lss-card p-4">
          <span className="lss-overline block text-emerald-700 flex items-center gap-1">
            <ArrowDownLeft className="w-3 h-3" /> Stock In
          </span>
          <span className={`text-2xl font-extrabold mt-1 block tabular ${
            Number(kpi.in) < 0 ? "text-rose-700" : "text-emerald-800"
          }`}>
            {fmtIn(kpi.in)}
          </span>
          <span className="text-[10px] font-bold text-slate-500 block truncate" title={kpi.in_value ? `Base purchase invoice cost: ${money(kpi.in_value)}` : "units"}>
            {kpi.in_value ? `Cost: ${money(kpi.in_value)}` : "units"}
          </span>
        </div>
        <div className="lss-card p-4">
          <span className="lss-overline block text-rose-700 flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" /> Stock Out
          </span>
          <span className="text-2xl font-extrabold text-rose-700 mt-1 block tabular">
            −{Number(kpi.out || 0).toLocaleString()}
          </span>
          <span className="text-[10px] font-bold text-slate-400">units</span>
        </div>
        <div className="lss-card p-4 bg-amber-50 border-amber-200">
          <span className="lss-overline block text-amber-800 flex items-center gap-1">
            <Package className="w-3 h-3" /> Closing
          </span>
          <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">
            {Number(kpi.closing || 0).toLocaleString()}
          </span>
          <span className="text-[10px] font-bold text-slate-500">units</span>
        </div>
        <div className="lss-card p-4">
          <span className="lss-overline block text-slate-500 flex items-center gap-1">
            <Scale className="w-3 h-3" /> Consumption Value
          </span>
          <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">
            {money(kpi.consumption_value)}
          </span>
          <span className="text-[10px] font-bold text-slate-500 block truncate" title={`COGS (Opening + In − Closing) = ${money(kpi.cogs_value ?? ((kpi.opening_value || 0) + (kpi.in_value || 0) - (kpi.value || 0)))}`}>
            COGS: {money(kpi.cogs_value ?? ((kpi.opening_value || 0) + (kpi.in_value || 0) - (kpi.value || 0)))}
          </span>
        </div>
      </div>

      {liveNote && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-950 leading-relaxed">
            <strong>Live stock differs by{" "}
              {Math.abs(Number(data.live.difference)).toLocaleString()} units.</strong>{" "}
            Ledger closing for today is{" "}
            {Number(summary.closing).toLocaleString()} units, while the Inventory
            Master shows {Number(data.live.stock).toLocaleString()} units. The gap
            comes from stock corrections saved directly on a product (stock audit
            or manual quantity edit), which do not create a ledger movement.
          </p>
        </div>
      )}

      {/* Table */}
      <div className="lss-card overflow-hidden">
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/60">
          <div className="text-[11px] font-bold text-slate-600">
            {filtered.length} of {rows.length} products
            {hasFilters ? " (filtered)" : ""}
          </div>
          <div className="text-[11px] font-bold text-slate-500">
            {data?.date_from || from} → {data?.date_to || to}
          </div>
        </div>

        <div className="overflow-x-auto" style={{ maxHeight: "65vh" }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-slate-950 text-amber-300">
              <tr className="text-[10px] uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-extrabold">Product</th>
                <th className="text-left px-3 py-3 font-extrabold">Category</th>
                <th className="text-right px-3 py-3 font-extrabold">Opening</th>
                <th className="text-right px-3 py-3 font-extrabold text-emerald-300">In</th>
                <th className="text-right px-3 py-3 font-extrabold text-rose-300">Out</th>
                <th className="text-right px-3 py-3 font-extrabold">Closing</th>
                <th
                  className="text-left px-3 py-3 font-extrabold"
                  title="Stock unit — bottle, packet, piece — not the measure inside it (ml / gm)"
                >
                  Stock Unit
                </th>
                <th className="text-right px-3 py-3 font-extrabold">Unit Price</th>
                <th className="text-right px-4 py-3 font-extrabold">Consumption Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-3" colSpan={9}>
                      <div className="h-4 bg-slate-100 rounded w-full" />
                    </td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center">
                    <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-500">
                      No products in this range.
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.sku_id || r.name}
                    className="hover:bg-amber-50/50 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <span
                        className="font-bold text-slate-900 block max-w-[320px] truncate"
                        title={r.name}
                      >
                        {r.name}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-600 uppercase">
                        {r.category || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-600">
                      {Number(r.opening).toLocaleString()}
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular font-semibold ${
                      Number(r.in) < 0 ? "text-rose-700" : "text-emerald-700"
                    }`} title={r.in_value ? `Inward cost: ${money(r.in_value)}` : undefined}>
                      {fmtIn(r.in)}
                      {Number(r.in) > 0 && r.in_value !== undefined ? (
                        <span className="block text-[10px] font-medium text-emerald-600/80">
                          {money(r.in_value)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular font-semibold text-rose-700">
                      {Number(r.out) ? `−${Number(r.out).toLocaleString()}` : "—"}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right tabular font-extrabold ${
                        Number(r.closing) < 0 ? "text-rose-700" : "text-slate-950"
                      }`}
                    >
                      {Number(r.closing).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{r.stock_unit || r.unit || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular text-slate-600">
                      {money(r.unit_price)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular font-bold text-slate-900">
                      {money(r.consumption_value)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && filtered.length > 0 && (
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="text-[11px] font-extrabold text-slate-900">
                  <td className="px-4 py-3" colSpan={2}>
                    Total · {filtered.length} products
                  </td>
                  <td className="px-3 py-3 text-right tabular">
                    {Number(filteredTotals.opening).toLocaleString()}
                  </td>
                  <td className={`px-3 py-3 text-right tabular ${
                    Number(filteredTotals.in) < 0 ? "text-rose-800" : "text-emerald-800"
                  }`} title={filteredTotals.in_value ? `Total inward cost: ${money(filteredTotals.in_value)}` : undefined}>
                    {fmtIn(filteredTotals.in)}
                    {filteredTotals.in_value ? (
                      <span className="block text-[10px] font-bold text-emerald-800">
                        {money(filteredTotals.in_value)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-right tabular text-rose-800">
                    −{Number(filteredTotals.out).toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-right tabular">
                    {Number(filteredTotals.closing).toLocaleString()}
                  </td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                  <td className="px-4 py-3 text-right tabular">
                    {money(filteredTotals.consumption)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 px-1">
        Opening quantities are frozen from the 21 Aug 2026 stock sheet. Movement
        dates are the dates saved on each purchase or stock-out, so a bill entered
        late still lands on its real day. Consumption value is units out × the
        current unit price — it values what was used, not the stock left behind.
      </p>
    </div>
  );
}
